import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import SEO, { canonicalFor, titleFor } from "../lib/seo";

const LAST_UPDATED = "June 2026";

const PageShell = ({ testid, kicker, title, accent, children }) => (
  <div data-testid={testid} className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-16">
    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--text-3)] mb-3">{kicker}</div>
    <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-6">
      {title} <span className="text-[color:var(--lime)]">{accent}</span>
    </h1>
    {children}
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-display text-xl md:text-2xl font-semibold mt-10 mb-3 text-[color:var(--text)]">{children}</h2>
);

const P = ({ children }) => <p className="text-[color:var(--text-2)] leading-relaxed mb-4">{children}</p>;

const Updated = () => (
  <div className="text-xs font-mono uppercase tracking-wider text-[color:var(--text-3)] mb-8">
    Last updated · {LAST_UPDATED}
  </div>
);

/* Reusable prominent health disclaimer banner — used on analysis/compare/scan pages */
export const HealthNotice = ({ compact = false }) => (
  <div
    data-testid="health-notice"
    className="surface rounded-xl p-4 flex items-start gap-3 border-l-4"
    style={{ borderLeftColor: "#FFB800", background: "rgba(255,184,0,0.04)" }}
  >
    <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#FFB800" }} strokeWidth={2} />
    <div className="text-xs md:text-sm text-[color:var(--text-2)] leading-relaxed">
      <span className="font-semibold text-[color:var(--text)]">AI-generated · Not medical advice.</span>{" "}
      This analysis is produced by an AI model and may be incomplete or inaccurate. Ingredients and recipes change —
      always read the actual product label and consult a qualified healthcare professional before making dietary or
      medical decisions{compact ? "." : ", especially regarding allergies or health conditions."}{" "}
      {!compact && (
        <Link to="/disclaimer" className="text-[color:var(--lime)] hover:underline whitespace-nowrap">
          Read the full disclaimer →
        </Link>
      )}
    </div>
  </div>
);

export const About = () => (
  <PageShell testid="about-page" kicker="About" title="Know exactly what you're" accent="eating.">
    <SEO
      title={titleFor("About")}
      description="WhatIsInside is a free AI-powered tool that gives traffic-light safety ratings for food ingredients, daily-intake warnings and healthier alternatives."
      canonical={canonicalFor("/about")}
    />
    <P>
      WhatIsInside is a free, AI-powered food ingredient analyzer. Search any product to see a traffic-light safety
      rating for every ingredient, plain-English daily-intake warnings, and healthier alternatives — no login, no email,
      no nonsense.
    </P>
    <P>
      Analyses are generated by Claude, an AI model, and cached so the next person gets answers in milliseconds. Because
      results are AI-generated, they are meant for general education and awareness — not as a substitute for the product
      label or professional advice.
    </P>
    <div className="mt-8">
      <HealthNotice />
    </div>
    <div className="flex flex-wrap gap-3 mt-8">
      <Link to="/" className="lime-btn inline-block rounded-lg px-4 py-2 text-sm" data-testid="about-home">
        Start analyzing
      </Link>
      <Link to="/disclaimer" className="ghost-btn inline-block rounded-lg px-4 py-2 text-sm">
        Disclaimer
      </Link>
    </div>
  </PageShell>
);

export const Disclaimer = () => (
  <PageShell testid="disclaimer-page" kicker="Important" title="Health" accent="disclaimer">
    <SEO
      title={titleFor("Disclaimer")}
      description="WhatIsInside provides AI-generated food ingredient information for educational purposes only. It is not medical, nutritional or professional advice."
      canonical={canonicalFor("/disclaimer")}
    />
    <Updated />

    <div className="mb-8">
      <HealthNotice compact />
    </div>

    <P>
      Please read this disclaimer carefully before relying on any information provided by WhatIsInside (the "Service").
      By using the Service you acknowledge and agree to the points below.
    </P>

    <H2>1. Educational purpose only</H2>
    <P>
      All content on WhatIsInside — including ingredient ratings, scores, warnings, "healthier alternatives", nutrition
      highlights and fun facts — is provided for general informational and educational purposes only. It is{" "}
      <span className="text-[color:var(--text)] font-semibold">not medical, nutritional, dietary, or professional advice</span>{" "}
      and must not be treated as such.
    </P>

    <H2>2. Not a substitute for professional advice</H2>
    <P>
      Always seek the advice of a qualified physician, registered dietitian, pharmacist or other healthcare professional
      with any questions about a medical condition, allergy, intolerance, medication interaction, pregnancy, or dietary
      change. Never disregard professional medical advice or delay seeking it because of something you read here. If you
      think you may be having a medical emergency or a severe allergic reaction, call your local emergency number
      immediately.
    </P>

    <H2>3. AI-generated content may be inaccurate</H2>
    <P>
      Analyses are generated by an artificial-intelligence language model. AI can and does make mistakes — it may produce
      information that is outdated, incomplete, or simply wrong. Product formulations, recipes, and labelling change
      frequently and vary by country and batch.{" "}
      <span className="text-[color:var(--text)] font-semibold">
        The product's actual packaging and ingredient label are always the authoritative source
      </span>
      . Please verify every ingredient, allergen and nutritional figure against the physical label before making any
      decision.
    </P>

    <H2>4. Allergies &amp; intolerances</H2>
    <P>
      Do not rely on WhatIsInside to determine whether a product is safe for an allergy or intolerance. We cannot detect
      cross-contamination, "may contain" warnings, or manufacturing changes. If you have a food allergy or intolerance,
      always check the official label and manufacturer information.
    </P>

    <H2>5. "Ratings" are opinions, not verdicts</H2>
    <P>
      Traffic-light ratings (red / amber / green) and numeric scores are simplified, generalised indicators intended to
      spark awareness. They are not endorsements, certifications, or statements of fact about any brand or product, and
      reasonable experts may disagree. Individual nutritional needs vary widely.
    </P>

    <H2>6. Third-party data &amp; links</H2>
    <P>
      Barcode lookups use the community-maintained OpenFoodFacts database, which may contain errors. The Service may link
      to third-party sites (including Amazon as an affiliate). We are not responsible for the content, accuracy, or
      practices of third-party sites or data sources.
    </P>

    <H2>7. No warranty &amp; limitation of liability</H2>
    <P>
      The Service is provided "as is" and "as available", without warranties of any kind, express or implied, including
      accuracy, completeness, fitness for a particular purpose, or non-infringement. To the fullest extent permitted by
      law, WhatIsInside and its operators shall not be liable for any loss, injury, or damage arising from your use of,
      or reliance on, the Service or its content.
    </P>

    <P className="text-sm text-[color:var(--text-3)] mt-8">
      By continuing to use WhatIsInside you confirm that you understand and accept this disclaimer. See also our{" "}
      <Link to="/terms" className="text-[color:var(--lime)] hover:underline">Terms of Use</Link> and{" "}
      <Link to="/privacy" className="text-[color:var(--lime)] hover:underline">Privacy Policy</Link>.
    </P>
  </PageShell>
);

export const Terms = () => (
  <PageShell testid="terms-page" kicker="Legal" title="Terms of" accent="Use">
    <SEO
      title={titleFor("Terms of Use")}
      description="The terms governing your use of WhatIsInside, a free AI-powered food ingredient analyzer."
      canonical={canonicalFor("/terms")}
    />
    <Updated />

    <P>
      Welcome to WhatIsInside. By accessing or using this website (the "Service") you agree to be bound by these Terms of
      Use. If you do not agree, please do not use the Service.
    </P>

    <H2>1. The Service</H2>
    <P>
      WhatIsInside is a free, informational tool that uses artificial intelligence to generate general safety and
      nutrition information about food products and ingredients. It requires no account and stores no personal profile.
    </P>

    <H2>2. Not professional advice</H2>
    <P>
      The Service does not provide medical, nutritional, or professional advice. All content is for general education
      only and is governed by our{" "}
      <Link to="/disclaimer" className="text-[color:var(--lime)] hover:underline">Disclaimer</Link>, which forms part of
      these Terms. You use the information entirely at your own risk.
    </P>

    <H2>3. Acceptable use</H2>
    <P>
      You agree to use the Service lawfully and not to: abuse, overload, scrape at scale, or disrupt the Service or its
      infrastructure; attempt to reverse-engineer or gain unauthorised access; or use it in any way that could harm the
      Service or other users. Automated request limits may apply.
    </P>

    <H2>4. Intellectual property</H2>
    <P>
      The WhatIsInside name, design, and original content are owned by its operators. Product names, brands, and
      trademarks referenced on the Service belong to their respective owners and are used for identification and
      descriptive purposes only; their mention does not imply any affiliation or endorsement.
    </P>

    <H2>5. Affiliate links &amp; advertising</H2>
    <P>
      The Service may earn commissions through affiliate links (including the Amazon Associates Program) and may display
      advertising. These do not affect the price you pay and do not influence our AI-generated ratings.
    </P>

    <H2>6. Third-party data</H2>
    <P>
      Barcode features rely on OpenFoodFacts and other third-party sources. We do not control and are not responsible for
      third-party data or websites.
    </P>

    <H2>7. Availability &amp; changes</H2>
    <P>
      We may modify, suspend, or discontinue any part of the Service at any time without notice. We may also update these
      Terms; continued use after changes constitutes acceptance of the updated Terms.
    </P>

    <H2>8. Limitation of liability</H2>
    <P>
      To the fullest extent permitted by law, the Service is provided "as is" without warranties, and the operators of
      WhatIsInside are not liable for any damages arising from your use of, or reliance on, the Service. Nothing in these
      Terms excludes liability that cannot be excluded by law.
    </P>

    <P className="text-sm text-[color:var(--text-3)] mt-8">
      See also our{" "}
      <Link to="/disclaimer" className="text-[color:var(--lime)] hover:underline">Disclaimer</Link> and{" "}
      <Link to="/privacy" className="text-[color:var(--lime)] hover:underline">Privacy Policy</Link>.
    </P>
  </PageShell>
);

export const Privacy = () => (
  <PageShell testid="privacy-page" kicker="Legal" title="Privacy" accent="Policy">
    <SEO
      title={titleFor("Privacy Policy")}
      description="How WhatIsInside handles data. We require no sign-in and collect no personal profile. Learn about AI processing, cookies, analytics and third parties."
      canonical={canonicalFor("/privacy")}
    />
    <Updated />

    <P>
      Your privacy matters. WhatIsInside is designed to be useful without collecting your personal information. This
      policy explains what limited data is involved when you use the Service.
    </P>

    <H2>1. No account, no personal profile</H2>
    <P>
      We do not require sign-in, and we do not ask for your name, email, or any identifying information to use the core
      Service.
    </P>

    <H2>2. Product searches &amp; AI processing</H2>
    <P>
      When you analyze a product, the product name (or a barcode you scan) is sent to our AI provider to generate the
      analysis. No personally identifying information is attached to these requests. Analyzed products are cached in our
      database so the next person searching the same product gets an instant response — this cache contains product data,
      not user data.
    </P>

    <H2>3. Local storage on your device</H2>
    <P>
      Features like Favorites and dismissing the "install app" prompt use your browser's local storage. This data stays
      on your device, is not sent to us, and you can clear it anytime through your browser settings.
    </P>

    <H2>4. Analytics</H2>
    <P>
      We may use privacy-friendly, aggregate analytics to understand how the Service is used (for example, which pages
      are popular). This helps us improve the product and is not used to build a personal profile of you.
    </P>

    <H2>5. Third-party services</H2>
    <P>
      The Service relies on a few third parties, each with its own privacy practices: our AI provider (for analysis),
      OpenFoodFacts (for barcode lookups), Amazon (affiliate links), and — where enabled — an advertising provider such as
      Google AdSense, which may use cookies to serve relevant ads. We encourage you to review those providers' policies.
    </P>

    <H2>6. Cookies</H2>
    <P>
      The core Service does not require advertising cookies to function. If advertising is enabled, third-party ad
      cookies may be set by the ad provider; you can manage cookies through your browser and, where applicable, ad
      settings.
    </P>

    <H2>7. Children</H2>
    <P>
      The Service is intended for general audiences and is not directed at children under 13. We do not knowingly collect
      personal information from children.
    </P>

    <H2>8. Changes</H2>
    <P>We may update this policy from time to time. The "last updated" date above reflects the latest version.</P>

    <P className="text-sm text-[color:var(--text-3)] mt-8">
      See also our{" "}
      <Link to="/terms" className="text-[color:var(--lime)] hover:underline">Terms of Use</Link> and{" "}
      <Link to="/disclaimer" className="text-[color:var(--lime)] hover:underline">Disclaimer</Link>.
    </P>
  </PageShell>
);

export const Trending = () => (
  <PageShell testid="trending-page" kicker="Live" title="Trending" accent="now">
    <SEO title={titleFor("Trending")} description="See what the community is analyzing on WhatIsInside." canonical={canonicalFor("/trending")} />
    <P>Check the homepage for live trending sections — most analyzed, best and worst rated, and the most-flagged additives this week.</P>
    <Link to="/" className="lime-btn inline-block rounded-lg px-4 py-2 text-sm mt-4" data-testid="trending-home">
      Back to home
    </Link>
  </PageShell>
);
